import { useEffect, useRef } from "react";
import { useDict } from "@/i18n/useDict";

const AIAssistantSection = () => {
  const { dict } = useDict();
  const a = dict.aiAssistant;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const featureIcons = ["📊", "🔍", "💬", "⚡"];
  const features = a.features.map((f, i) => ({ ...f, icon: featureIcons[i] }));

  return (
    <section className="py-20 md:py-32 px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side: Text content */}
          <div className="animate-on-scroll">
            <div className="mb-6">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full">
                {a.badge}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-4">
              {a.titleLine1}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">
                {a.titleHighlight}
              </span>
              {a.titleTrail}
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {a.introPre}<strong className="text-foreground">{a.introStrong}</strong>{a.introPost}
            </p>

            {/* Features list */}
            <div className="space-y-4">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4">
                  <div className="text-2xl flex-shrink-0">{feature.icon}</div>
                  <div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#preturi"
              className="inline-flex items-center justify-center rounded-xl px-8 py-3 mt-8 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {a.cta}
            </a>
          </div>

          {/* Right side: Chat preview */}
          <div className="animate-on-scroll">
            <div
              className="rounded-2xl p-6 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0a1628 0%, #0f2548 50%, #0d3a6e 100%)",
                boxShadow: "0 20px 60px rgba(30, 130, 255, 0.15)",
              }}
            >
              {/* Chat header bar */}
              <div className="mb-4 pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white/25 rounded-full"></div>
                  <div className="w-2 h-2 bg-white/25 rounded-full"></div>
                  <div className="w-2 h-2 bg-white/25 rounded-full"></div>
                  <span className="text-[10px] font-bold text-white/40 tracking-widest ml-2 uppercase">
                    {a.chatTag}
                  </span>
                </div>
              </div>

              {/* Chat messages */}
              <div className="space-y-3">
                {/* User message 1 */}
                <div className="flex justify-end">
                  <div
                    className="max-w-xs rounded-3xl px-4 py-2 text-sm"
                    style={{
                      background: "#1e82ff",
                      color: "#ffffff",
                    }}
                  >
                    {a.chatQ1}
                  </div>
                </div>

                {/* AI response 1 */}
                <div className="flex justify-start">
                  <div
                    className="max-w-xs rounded-3xl px-4 py-2 text-sm"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      color: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    {a.chatA1Pre}
                    <strong style={{ color: "#4db8ff" }}>{a.chatA1Name}</strong>{a.chatA1Post}
                  </div>
                </div>

                {/* User message 2 */}
                <div className="flex justify-end mt-3">
                  <div
                    className="max-w-xs rounded-3xl px-4 py-2 text-sm"
                    style={{
                      background: "#1e82ff",
                      color: "#ffffff",
                    }}
                  >
                    {a.chatQ2}
                  </div>
                </div>

                {/* AI response 2 */}
                <div className="flex justify-start">
                  <div
                    className="max-w-xs rounded-3xl px-4 py-2 text-sm"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      color: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    {a.chatA2Pre}
                    <strong style={{ color: "#4db8ff" }}>{a.chatA2Name}</strong>{a.chatA2Mid}
                    <strong style={{ color: "#4db8ff" }}>{a.chatA2Amount}</strong>{a.chatA2Post}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAssistantSection;
